import ApiSauce, { CancelToken, ApisauceConfig, ApiResponse } from 'apisauce';
import SessionHelper from './Session';
import ApiConfig from './config/ApiConfig';
import { AuthContext } from './config/AdalConfig';
import AdalConfig from './config/AdalEnvConfig';
import * as urls from '../components/shared/urls';
import moment from 'moment-timezone';
import { generateUUID } from 'util';
// set up cancel token from axios to be able to cancel network requests
const cancelSource = CancelToken.source(); // more info: https://github.com/axios/axios#cancellation
const ApiConfigWithCancelToken = {
  ...ApiConfig,
  cancelToken: cancelSource.token
};
const axios = require('axios').default;

const instance = ApiSauce.create(ApiConfigWithCancelToken);


instance.axiosInstance.interceptors.request.use( x => {
  // to avoid overwriting if another interceptor
  // already defined the same object (meta)  
  try {    
    x.data = x.data || {}
    if (typeof x.data === 'string')
    {
      var parsed = JSON.parse(x.data);
      parsed.requestStartedAt = new Date().getTime();
      x.data = JSON.stringify(parsed);
    } else if (typeof x.data === 'number')
    {
      x.data = {id: x.data};
      x.data.requestStartedAt = new Date().getTime();
    } else {
      x.data.requestStartedAt = new Date().getTime();
    }
  } catch(ex) {
    console.log('error logging for sla', ex);
  }
  
  return x;
});

instance.axiosInstance.interceptors.response.use(async x => {
  // NOTE: A response interceptor MUST always return the response object.
  // axios 1.x / apisauce 3.x propagate whatever this returns; returning
  // `undefined` (a bare `return;`) breaks the downstream chain and causes
  // "Cannot read properties of undefined (reading 'config')". Always `return x`.
  try {
    if (x?.config?.url?.includes('performance')) return x;

    // In axios 1.x, config.data may already be an object (not a JSON string).
    const rawData = x?.config?.data;
    const dataParsed = typeof rawData === 'string' ? JSON.parse(rawData) : (rawData || {});
    const executionTime = new Date().getTime() - dataParsed.requestStartedAt;
    const url = 'performance/SaveResponseTime';
    const perfObj = {url: x.config.url, milliseconds: executionTime, requestid: x.headers["request-id"]}

    await instance.post(url, perfObj);
  } catch(ex) {
    console.log('error logging for sla', ex);
  }
  return x;
});


// Add a request interceptor
instance.addAsyncRequestTransform(
  (config: any) => {
    // Check and acquire a token before the request is sent
    return new Promise((resolve, reject) => {
      AuthContext.acquireToken(
        AdalConfig.endpoints.api,
        (message, token, msg) => {
          if (!!token) {
            config.headers.Authorization = `Bearer ${token}`;
            resolve(config);
          } else {
            // Do something with error of acquiring the token
            reject(config);
          }
        }
      );
    });
  }  
);

const systemGeneratedDates = ["createdDate", "modifiedDate", "startedDate", 
"sentToExperianDate", 
"dataGeneratedDate", "sentToCarfaxDate", "sentToTruckBusDate", "sentToNhtsa", 
"sentToLawFirm", "sentToNdotSftp"]
// switch dates from UTC to Central time
instance.addAsyncResponseTransform(async response => {
  //console.debug("tx response", response);
  try {
    if(response && response.headers && response.headers['content-type']
    && response.headers['content-type'] == "application/json; charset=utf-8"
    && response.config && response.config.url && response.config.url.includes("/api/")
    && !response.config.url.includes("localhost")) // local 'server' is already the right time
    {
      //console.debug("json response", response);
      systemGeneratedDates.forEach((propertyName, index) => {
        fixSystemDates(response.data, propertyName);
    });
    }
  } catch(ex) {
    console.debug("error with repsonse: ", response)
  }
});

function fixSystemDates(object, key, path = "") {
    Object.keys(object).some(function(k) {
        if (k === key && object[k]) {
            //console.debug(path + " - " + key, object[k]);            
            try {
              var a = moment.utc(object[k]).tz("America/Chicago");
              object[k] = a.format();
              //console.debug(path + " - " + key + " updated", object[k]);  
            } catch (ex) {
              //console.debug(path + " - " + key + "error", ex);
            }
        }
        if (object[k] && typeof object[k] === 'object') {
            fixSystemDates(object[k], key, path + " " + k);
        }
    });
}

// Add a monitor so that after each call the expiry and the timeout is reset
const timeoutMonitor = response => {
  // reset the expiry
  SessionHelper.setExpiry();
  // and reset the timeout function
  SessionHelper.resetExpiryTimeout();
};
instance.addMonitor(timeoutMonitor);

// Add a check before each request, to see if our session expiry isn't passed and if so, abort the request and log out
instance.addRequestTransform(request => {
  // check if token has expired or is null.
  // If it is null, it means that it has already expired in another tab of the browser and the user has already been logged out.
  if (SessionHelper.isTokenExpiredOrNull) {
    // clear the session helper
    SessionHelper.removeExpiry();
    SessionHelper.stopExpiryTimeout();
    // log out user
    AuthContext.logOut();
    // cancel the request because the logout is in a race with the request being sent out
    cancelSource.cancel();
  }
});

instance.addAsyncRequestTransform(request => {
  return new Promise((resolve, reject) => {
    const thisUrl = request.url;
    if(request.method == 'post' && urls.SAVE_URLS.some(x => thisUrl.includes(x)))
    {  
      const recentSavesStr = window.localStorage.getItem('recentSaves');
      if(recentSavesStr) {
        const recentSaves = JSON.parse(recentSavesStr);
        const matchingSaves = recentSaves.filter(x => x.url == thisUrl);
        if(matchingSaves && matchingSaves.length > 0) {
          const seconds = (Date.now() - matchingSaves[0].timeStamp) / 1000;
          if(seconds < 5)
          {
            reject('Detected duplicate save in under 5 seconds');
          }
        } 
      } 
      const saveRequest = {url: thisUrl, timeStamp: Date.now()};
      const newRecentSaves = JSON.parse(recentSavesStr) ?? [];
      const recentSavesWithOldRemoved = newRecentSaves.filter(x => {
        if((thisUrl !== x.url) && ((Date.now() - x.timeStamp) / 1000) < 5) {
          return x;
        }
      });
      recentSavesWithOldRemoved.push(saveRequest);
      window.localStorage.setItem('recentSaves', JSON.stringify(recentSavesWithOldRemoved));    
      resolve();
    } else {          
      resolve();
    }
  }) 
});

function uuidv4() {
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
    (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> +c / 4).toString(16)
  );
}

export default instance;
